import { te } from 'date-fns/locale';
import orderBy from 'lodash/orderBy';
import type { Thumbnail } from '~/Features/MyPageApp/Application/Components/Block/RecommendedContentBlockContent/types';
import type { AspectRatio } from '~/Partials/Picture/AspectRatios';

interface AllowedRatio {
    name: AspectRatio;
    value: number;
}

const allowedRatios: AllowedRatio[] = [
    { name: '1x1', value: 1 },
    { name: '2x3', value: 2 / 3 },
    { name: '3x2', value: 3 / 2 },
    { name: '3x4', value: 3 / 4 },
    { name: '4x3', value: 4 / 3 },
    { name: '9x16', value: 9 / 16 },
    { name: '16x9', value: 16 / 9 },
];

const sortedRatios = orderBy(allowedRatios, ratio => ratio.value);

export const resolveAspectRatio = (width: number | undefined, height: number | undefined): AspectRatio | undefined => {
    if (!width || !height) {
        return undefined;
    }

    const ratio = width / height;

    let previous: AllowedRatio | null = null;

    for (const potentialRatio of sortedRatios) {
        if (potentialRatio.value > ratio) {
            if (!previous) {
                return potentialRatio.name;
            }

            return potentialRatio.value - ratio > ratio - previous.value ? previous.name : potentialRatio.name;
        }

        previous = potentialRatio;
    }

    return previous?.name;
};

export const getThumbnailBasedOnTargetSize = (
    thumbnails: Thumbnail[],
    targetWidth: number,
    targetHeight: number,
    threshold: number = 100
): Thumbnail | undefined => {
    if (!thumbnails || thumbnails.length === 0) {
        return undefined;
    }

    const thresholdWidth = targetWidth + threshold;
    const thresholdHeight = targetHeight + threshold;

    const thumbnailsMatchingTargetSizeWithThreshold = thumbnails.filter(
        thumbnail => thumbnail.width >= thresholdWidth && thumbnail.height >= thresholdHeight
    );

    if (thumbnailsMatchingTargetSizeWithThreshold.length > 0) {
        return orderBy(thumbnailsMatchingTargetSizeWithThreshold, thumbnail => thumbnail.width * thumbnail.height)[0];
    }

    const thumbnailEqualOrBiggerThanTargetSize = thumbnails.find(
        thumbnail => thumbnail.width >= targetWidth && thumbnail.height >= targetHeight
    );

    if (thumbnailEqualOrBiggerThanTargetSize) {
        return thumbnailEqualOrBiggerThanTargetSize;
    }

    const thumbnailsSortedByLargestArea = orderBy(thumbnails, thumbnail => thumbnail.width * thumbnail.height, 'desc');

    return thumbnailsSortedByLargestArea[0];
};
